import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
    Avatar,
    Badge,
    Box,
    Button,
    Typography,
} from "@mui/material";
import { PeopleOutlined, Check, Close, MessageOutlined } from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import {
    DIALOG_CANCEL_BUTTON_SX,
    DIALOG_PAPER_RADIUS,
} from "../common/dialogActionButtonSx";
import { ACCOUNT_PAGE_PREMIUM_CTA_SX } from "../account/accountPageStyles";
import { colors } from "../../theme/colors";
import BackdropOverlay from "../common/BackdropOverlay.jsx";
import { ProfileRankBadge } from "../profile/profileIdentityBadges";
import {
    formatInviteElo,
    formatPartyCountLabel,
} from "./inviteDialogUtils";

const COUNTDOWN_RADIUS = 44;
const COUNTDOWN_VIEW_CIRCUMFERENCE = 2 * Math.PI * COUNTDOWN_RADIUS;
const ELO_FONT = "'JetBrains Mono', monospace";
const DIALOG_FADE_IN = {
    "0%": { opacity: 0 },
    "100%": { opacity: 1 },
};

const PAPER_SX = {
    "@keyframes dialogFadeIn": { ...DIALOG_FADE_IN },
    position: "relative",
    width: { xs: "100%", sm: "25.5rem", xl: "26.5rem" },
    maxWidth: "38.5rem",
    overflow: "hidden",
    background: `
        linear-gradient(
            165deg,
            rgba(18,14,14,0.98) 0%,
            rgba(10,10,12,0.99) 48%,
            rgba(8,8,10,1) 100%
        )
    `,
    borderRadius: DIALOG_PAPER_RADIUS,
    border: `1px solid ${colors.border.default}`,
    boxShadow: "0 28px 64px rgba(0, 0, 0, 0.55)",
    animation: "dialogFadeIn 0.2s ease-in-out",
    "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: "12%",
        right: "12%",
        height: "1px",
        background: `linear-gradient(90deg, transparent, ${alpha(colors.accent.main, 0.55)}, transparent)`,
        pointerEvents: "none",
    },
};

const SURFACE_SX = {
    borderRadius: "8px",
    backgroundColor: colors.background.surface,
    border: `1px solid ${colors.border.default}`,
};

const DECLINE_BUTTON_SX = {
    ...DIALOG_CANCEL_BUTTON_SX,
    flex: "1 1 0",
    minWidth: 0,
    height: "2.65rem",
    gap: "0.4rem",
    boxSizing: "border-box",
    border: "1px solid transparent",
    "&:hover:not(:disabled)": {
        color: "#FCA5A5",
        borderColor: "rgba(239,68,68,0.24)",
        background:
            "linear-gradient(180deg, rgba(239,68,68,0.1), rgba(239,68,68,0.055))",
        boxShadow:
            "inset 0 1px 0 rgba(255,255,255,0.035), 0 8px 20px rgba(0,0,0,0.2)",
    },
};

const ACCEPT_BUTTON_SX = {
    ...ACCOUNT_PAGE_PREMIUM_CTA_SX,
    flex: "1.15 1 0",
    minWidth: 0,
    width: "auto",
    height: "2.65rem",
};

function InviteCircularCountdown({ timeLeft, totalSeconds }) {
    const ratio =
        totalSeconds > 0
            ? Math.min(1, Math.max(0, timeLeft / totalSeconds))
            : 0;
    const dashOffset = COUNTDOWN_VIEW_CIRCUMFERENCE * (1 - ratio);

    return (
        <Box
            sx={{
                ml: "auto",
                flexShrink: 0,
                position: "relative",
                width: { xs: "3.1rem", xl: "3.45rem" },
                height: { xs: "3.1rem", xl: "3.45rem" },
            }}
        >
            <Box
                component="svg"
                viewBox="0 0 100 100"
                aria-hidden
                sx={{
                    width: "100%",
                    height: "100%",
                    display: "block",
                    transform: "rotate(-90deg)",
                }}
            >
                <circle
                    cx={50}
                    cy={50}
                    r={COUNTDOWN_RADIUS}
                    fill="none"
                    stroke={alpha(colors.text.secondary, 0.28)}
                    strokeWidth={5}
                />
                <circle
                    cx={50}
                    cy={50}
                    r={COUNTDOWN_RADIUS}
                    fill="none"
                    stroke={colors.primary.main}
                    strokeWidth={5}
                    strokeLinecap="round"
                    strokeDasharray={COUNTDOWN_VIEW_CIRCUMFERENCE}
                    strokeDashoffset={dashOffset}
                    style={{
                        filter: `drop-shadow(0 0 6px ${alpha(colors.primary.main, 0.55)})`,
                        transition: "stroke-dashoffset 0.95s linear",
                    }}
                />
            </Box>
            <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
                <Typography
                    component="span"
                    sx={{
                        position: "absolute",
                        left: "50%",
                        top: "50%",
                        transform: "translate(-50%, -50%)",
                        m: 0,
                        p: 0,
                        fontSize: { xs: "1rem", xl: "1.15rem" },
                        fontWeight: 700,
                        color: colors.primary.main,
                        lineHeight: 1,
                        textShadow: `0 0 10px ${alpha(colors.primary.main, 0.45)}`,
                        fontVariantNumeric: "tabular-nums",
                        display: "block",
                        textAlign: "center",
                    }}
                >
                    {timeLeft}
                </Typography>
            </Box>
        </Box>
    );
}

InviteCircularCountdown.propTypes = {
    timeLeft: PropTypes.number.isRequired,
    totalSeconds: PropTypes.number.isRequired,
};

export default function InviteDialog({
    open,
    inviteData,
    onDecline,
    onAccept,
    countdown = 60000,
}) {
    const totalSeconds = Math.max(1, Math.floor(countdown / 1000));
    const [timeLeft, setTimeLeft] = useState(totalSeconds);

    useEffect(() => {
        if (!open || !inviteData) return;
        setTimeLeft(totalSeconds);
        const id = setInterval(
            () => setTimeLeft((prev) => Math.max(0, prev - 1)),
            1000
        );
        return () => clearInterval(id);
    }, [open, totalSeconds, inviteData]);

    useEffect(() => {
        if (!open || !inviteData) return undefined;
        const onKeyDown = (event) => {
            if (event.key === "Escape") {
                onDecline(inviteData.inviteId, inviteData);
            }
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [inviteData, onDecline, open]);

    if (!inviteData) return null;

    const partySize = Math.max(
        Number(inviteData.partySize) || 0,
        Array.isArray(inviteData.partyMembers)
            ? inviteData.partyMembers.length
            : 0
    );
    const eloLabel = formatInviteElo(inviteData.fromUserElo);

    const handleDecline = () => {
        onDecline(inviteData.inviteId, inviteData);
    };

    const handleAccept = () => {
        onAccept(inviteData.inviteId, inviteData);
    };

    return (
        <BackdropOverlay open={open}>
            <Box
                role="dialog"
                aria-modal="true"
                aria-label="Team invite"
                sx={PAPER_SX}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "0.55rem",
                        px: { xs: "1.35rem", sm: "1.6rem" },
                        pt: "1.45rem",
                        pb: "1.15rem",
                    }}
                >
                    <PeopleOutlined
                        sx={{
                            mt: "0.02rem",
                            flexShrink: 0,
                            fontSize: { xs: "1.05rem", xl: "1.12rem" },
                            color: colors.accent.main,
                        }}
                    />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                            sx={{
                                color: colors.accent.main,
                                fontSize: { xs: "0.64rem", xl: "0.68rem" },
                                fontWeight: 700,
                                letterSpacing: "0.14em",
                                textTransform: "uppercase",
                                lineHeight: 1.2,
                            }}
                        >
                            Team Invite
                        </Typography>
                        <Typography
                            sx={{
                                mt: "0.18rem",
                                color: colors.text.primary,
                                fontSize: { xs: "1.2rem", xl: "1.35rem" },
                                fontWeight: 700,
                                letterSpacing: "0.01em",
                                lineHeight: 1.15,
                            }}
                        >
                            Join the party
                        </Typography>
                        <Typography
                            sx={{
                                mt: "0.22rem",
                                color: colors.text.secondary,
                                fontSize: { xs: "0.78rem", xl: "0.82rem" },
                                fontWeight: 400,
                                lineHeight: 1.4,
                            }}
                        >
                            You've been invited to queue together.
                        </Typography>
                    </Box>
                    <InviteCircularCountdown
                        timeLeft={timeLeft}
                        totalSeconds={totalSeconds}
                    />
                </Box>

                <Box
                    sx={{
                        height: "1px",
                        width: "100%",
                        backgroundColor: colors.border.default,
                    }}
                />

                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "0.7rem",
                        px: { xs: "1.35rem", sm: "1.6rem" },
                        pt: "1.1rem",
                        pb: "0.2rem",
                    }}
                >
                    <Box
                        sx={{
                            ...SURFACE_SX,
                            display: "flex",
                            alignItems: "center",
                            gap: "0.9rem",
                            p: { xs: "0.75rem", xl: "0.85rem" },
                        }}
                    >
                        <Badge
                            overlap="circular"
                            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                            variant="dot"
                            sx={{
                                flexShrink: 0,
                                "& .MuiBadge-badge": {
                                    minWidth: { xs: 8, xl: 10 },
                                    width: { xs: 8, xl: 10 },
                                    height: { xs: 8, xl: 10 },
                                    borderRadius: "50%",
                                    backgroundColor: colors.state.success.main,
                                    boxShadow: `0 0 0 2px ${colors.background.surface}`,
                                    border: "none",
                                },
                            }}
                        >
                            <Avatar
                                src={inviteData.fromUserPicture || undefined}
                                alt={inviteData.fromUserName}
                                sx={{
                                    width: { xs: "2.75rem", xl: "3rem" },
                                    height: { xs: "2.75rem", xl: "3rem" },
                                }}
                            />
                        </Badge>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "0.45rem",
                                    minWidth: 0,
                                }}
                            >
                                <Typography
                                    sx={{
                                        color: colors.text.primary,
                                        fontSize: { xs: "1.02rem", xl: "1.12rem" },
                                        fontWeight: 700,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        lineHeight: 1.2,
                                    }}
                                >
                                    {inviteData.fromUserName}
                                </Typography>
                                <ProfileRankBadge
                                    rank={inviteData.fromUserRank}
                                    size="compact"
                                />
                            </Box>
                            <Box
                                sx={{
                                    mt: "0.28rem",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "0.75rem",
                                    minWidth: 0,
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "baseline",
                                        gap: "0.32rem",
                                        flexShrink: 0,
                                    }}
                                >
                                    <Typography
                                        component="span"
                                        sx={{
                                            fontFamily: ELO_FONT,
                                            fontSize: { xs: "0.98rem", xl: "1.05rem" },
                                            fontWeight: 600,
                                            letterSpacing: "0.02em",
                                            lineHeight: 1,
                                            color: colors.text.primary,
                                            fontVariantNumeric: "tabular-nums",
                                        }}
                                    >
                                        {eloLabel || "—"}
                                    </Typography>
                                    <Typography
                                        component="span"
                                        sx={{
                                            fontSize: "0.58rem",
                                            fontWeight: 650,
                                            letterSpacing: "0.1em",
                                            textTransform: "uppercase",
                                            color: alpha(colors.text.secondary, 0.52),
                                            lineHeight: 1,
                                        }}
                                    >
                                        ELO
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "0.28rem",
                                        minWidth: 0,
                                        color: colors.text.secondary,
                                    }}
                                >
                                    <PeopleOutlined
                                        sx={{
                                            fontSize: "0.85rem",
                                            color: colors.text.secondary,
                                        }}
                                    />
                                    <Typography
                                        component="span"
                                        sx={{
                                            fontSize: { xs: "0.62rem", xl: "0.66rem" },
                                            fontWeight: 600,
                                            letterSpacing: "0.03em",
                                            lineHeight: 1,
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        Party
                                    </Typography>
                                    <Box
                                        aria-hidden
                                        sx={{
                                            width: "3px",
                                            height: "3px",
                                            borderRadius: "50%",
                                            backgroundColor: alpha(
                                                colors.text.secondary,
                                                0.45
                                            ),
                                        }}
                                    />
                                    <Typography
                                        component="span"
                                        sx={{
                                            fontSize: { xs: "0.62rem", xl: "0.66rem" },
                                            fontWeight: 600,
                                            letterSpacing: "0.03em",
                                            lineHeight: 1,
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {formatPartyCountLabel(partySize)}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            ...SURFACE_SX,
                            display: "flex",
                            alignItems: "center",
                            gap: "0.55rem",
                            px: { xs: "0.75rem", xl: "0.85rem" },
                            py: { xs: "0.55rem", xl: "0.6rem" },
                        }}
                    >
                        <Box
                            aria-hidden
                            sx={{
                                width: { xs: "1.45rem", xl: "1.6rem" },
                                height: { xs: "1.45rem", xl: "1.6rem" },
                                flexShrink: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderRadius: "7px",
                                border: `1px solid ${alpha(colors.primary.main, 0.22)}`,
                                background: `
                                    linear-gradient(
                                        180deg,
                                        ${alpha(colors.primary.main, 0.14)} 0%,
                                        ${alpha(colors.primary.main, 0.06)} 100%
                                    )
                                `,
                            }}
                        >
                            <MessageOutlined
                                sx={{
                                    fontSize: { xs: "0.82rem", xl: "0.9rem" },
                                    color: alpha(colors.accent.soft, 0.88),
                                }}
                            />
                        </Box>
                        <Typography
                            sx={{
                                fontSize: { xs: "0.72rem", xl: "0.8rem" },
                                color: colors.text.secondary,
                                textAlign: "left",
                                lineHeight: 1.35,
                                fontStyle: "italic",
                            }}
                        >
                            <Box
                                component="span"
                                sx={{
                                    color: colors.text.primary,
                                    fontWeight: 700,
                                    fontStyle: "normal",
                                }}
                            >
                                {inviteData.fromUserName}
                            </Box>
                            {" wants you to join his team."}
                        </Typography>
                    </Box>
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        px: { xs: "1.35rem", sm: "1.6rem" },
                        pt: "1.15rem",
                        pb: "1.35rem",
                        gap: "0.75rem",
                    }}
                >
                    <Button onClick={handleDecline} sx={DECLINE_BUTTON_SX}>
                        <Close sx={{ fontSize: "1rem", color: "inherit" }} />
                        Decline
                    </Button>
                    <Button
                        variant="contained"
                        disableElevation
                        onClick={handleAccept}
                        sx={ACCEPT_BUTTON_SX}
                    >
                        <Check sx={{ fontSize: "1.05rem", color: "inherit" }} />
                        Accept
                    </Button>
                </Box>
            </Box>
        </BackdropOverlay>
    );
}

InviteDialog.propTypes = {
    open: PropTypes.bool,
    inviteData: PropTypes.object,
    onDecline: PropTypes.func.isRequired,
    onAccept: PropTypes.func.isRequired,
    countdown: PropTypes.number,
};
